import { Slider } from '../../app/common/sharedComponents/Slider';
import { GaugeChart } from '../../app/common/sharedComponents/GaugeChart';
import { getComparedToAverageClassStyle } from '../../helpers/charts';
import { Legend } from '../../app/common/sharedComponents/Legend';
import React from 'react';

export interface IOverallScore {
  compSetAverageScore: number;
  competingLocation: string;
  name: string;
  rankingPosition: number;
  rankingTotal: number;
  score: number;
  type: string;
  change?: number;
}

interface GaugeGraphsSliderProps {
  overallScores: IOverallScore[];
}

const EQ_SCORE_TYPE = 'eqScore';

export function GaugeGraphsSlider ({ overallScores }: GaugeGraphsSliderProps) {
  const getEqScoreColor = (score: number): string => {
    const roundedScore = Math.round(score);

    const eqColorMap: { [key: number]: string } = {
      1: '#D75426',
      2: '#E16315',
      3: '#C97816',
      4: '#717A36',
      5: '#427F3A',
    };

    return eqColorMap[roundedScore] || eqColorMap[5];
  };

  const getComparedScoreColor = (scoreDifference: number): string => {
    const style = getComparedToAverageClassStyle(scoreDifference);

    const colorMap: { [key: string]: string } = {
      success: '#4fada6',
      warning: '#f2cb15',
      danger: '#ef374e',
    };

    return colorMap[style] || '#d9d9d9';
  };

  const getGaugeColor = (overall: IOverallScore): string => {
    if (overall.type === EQ_SCORE_TYPE) {
      return getEqScoreColor(overall.score);
    }

    const scoreDifference = overall.score - overall.compSetAverageScore;
    return getComparedScoreColor(scoreDifference);
  };

  const nth = (d: number): string => {
    if (d > 3 && d < 21) {
      return 'th';
    }
    switch (d % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const renderChangeLabel = (change: number | undefined) => {
    return (
      <div
        className={`${
          change && change > 0 ? 'color-4FADA6' : 'color-EF374E'
        } piechartTitleChange`}
      >
        <span
          className={`${
            change && change > 0 ? 'background-EEF9F8' : 'background-FDEBED'
          } piechartTitleChangeSpan`}
        >
          <span className="piechartTitleChangeSpanIcon">
            {change && change > 0 ? '\u25B2' : '\u25BC'}
          </span>
          <span>
            {change && change > 0 ? '+' : ''}
            {change ? change.toFixed(1) : 0}
            {'%'}
          </span>
        </span>
      </div>
    );
  };

  return (
    <>
      {overallScores && overallScores.length > 0 && (
        <Slider
          initialSlide={0}
          dots={true}
          infinite={true}
          speed={500}
          slidesToShow={1}
          content={overallScores.map((overall: IOverallScore) => (
            <React.Fragment key={overall.name}>
              <div className="row-separater">
                <div className="property-display1">
                  <GaugeChart
                    value={overall.score}
                    min={overall.type === EQ_SCORE_TYPE ? 1 : undefined}
                    max={overall.type === EQ_SCORE_TYPE ? 5 : undefined}
                    color={getGaugeColor(overall)}
                    isShowPercentage={overall.type !== EQ_SCORE_TYPE}
                    isEqScore={overall.type === EQ_SCORE_TYPE}
                    decimals={overall.type === EQ_SCORE_TYPE ? 1 : 0}
                  />
                  {overall.type !== EQ_SCORE_TYPE &&
                    renderChangeLabel(overall.change)}
                </div>
                <div className="property-display2">
                  <div className="piechartTitle">{overall.name}</div>
                  <div className="piechartSubTitle">
                    {overall.rankingPosition}
                    {nth(overall.rankingPosition)}/
                    <span className="piechartSubTitleGrey">
                      {overall.rankingTotal}
                    </span>
                  </div>
                  <div className="piechartSubTitle2">Competing Hotels</div>
                </div>
              </div>
              {overall.type !== EQ_SCORE_TYPE && (
                <Legend
                  data={[
                    {
                      label: 'Above avg.',
                      intent: 'success',
                    },
                    {
                      label: 'Within 2%',
                      intent: 'warning',
                    },
                    {
                      label: 'Below avg.',
                      intent: 'danger',
                    },
                    {
                      label: 'No Comp Data',
                      intent: 'default',
                    },
                  ]}
                />
              )}
            </React.Fragment>
          ))}
        />
      )}
    </>
  );
}
